import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { Tradable } from "@models/interfaces/tradable";
import { Fiat } from "@models";
import { Index } from "@models/asset/index/index.model";
import { Router } from "@angular/router";
import { MIN_PERCENTAGE_DISPLAY } from "@helpers/constants";
import { toCurrencyByFiatId } from "@helpers/tradable-convert";
import { AccountService } from "@modules/account/services/account.service";
import { getPriceChange } from "@helpers/utils";
import { ValueChangePrefixInterface } from "@modules/core/pipes/value-change.pipe";
import { FiatPrecisionOptions } from "@modules/core/pipes";
import { PathResolverService } from "@main-services/path-resolver.service";
import { WatchlistFacade } from "@modules/watchlist/domain/+state/watchlist.facade";
import { Timespan } from "@helpers/const/timespan";

@Component({
	selector: "watchlist-widget-list",
	templateUrl: "./watchlist-widget-list.component.html",
	styleUrls: ["./watchlist-widget-list.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class WatchlistWidgetListComponent {
	public readonly timespan: Timespan = Timespan.Day;

	public watchedAssets: WatchlistAssetViewModel[] = [];

	@Input()
	public set list(tradables: Tradable[]) {
		this.watchedAssets = tradables.map(
			(tradable) =>
				({
					logo: tradable.getLogo(),
					defaultLogo: tradable.getDefaultLogo(),
					name: tradable.getName(),
					symbol: tradable.getSymbol(false, true),
					colorClass: this.getColorClass(tradable),
					valueChangePrefixOptions: this.getValueChangePrefixOptions(tradable),
					absoluteChange: Math.abs(tradable.getChange24h()),
					fiatAmount: this.getFiatAmount(tradable),
					fiatPrecisionOptions: this.getFiatPrecisionOptions(tradable),
					tradable
				}) as WatchlistAssetViewModel
		);
	}

	@Output() public watchlistUpdated: EventEmitter<void> = new EventEmitter<void>();

	private getColorClass(tradable: Tradable): { chart: string; price: string } {
		return this.isChangeNegative(tradable)
			? {
					chart: "decreaseColor",
					price: "red-text"
				}
			: this.isChangePositive(tradable)
				? {
						chart: "increaseColor",
						price: "green-text"
					}
				: {
						chart: "noChangeColor",
						price: ""
					};
	}

	private getValueChangePrefixOptions(tradable: Tradable): ValueChangePrefixInterface {
		return {
			isProfit: this.isChangePositive(tradable),
			noChange: !this.isChangePositive(tradable) && !this.isChangeNegative(tradable)
		};
	}

	private getFiatPrecisionOptions(tradable: Tradable): Partial<FiatPrecisionOptions> {
		const fiat = this.getFiat();
		return {
			id: fiat?.getId(),
			currencies: [fiat, tradable],
			symbol: true,
			removeCurrency: this.isIndex(tradable)
		} as Partial<FiatPrecisionOptions>;
	}

	public constructor(
		protected accountService: AccountService,
		protected router: Router,
		private readonly pathResolverService: PathResolverService,
		private readonly watchlistFacade: WatchlistFacade
	) {}

	public getChangeByTime(tradable: Tradable): number {
		return getPriceChange(tradable, this.timespan).changePercent;
	}

	public isChangePositive(tradable: Tradable): boolean {
		return +this.getChangeByTime(tradable) >= MIN_PERCENTAGE_DISPLAY || +this.getChangeAmount(tradable) > 0;
	}

	public isChangeNegative(tradable: Tradable): boolean {
		return +this.getChangeByTime(tradable) <= -MIN_PERCENTAGE_DISPLAY || +this.getChangeAmount(tradable) < 0;
	}

	public getChangeAmount(tradable: Tradable): number {
		return getPriceChange(tradable, this.timespan).changeAmount;
	}

	public getFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public getFiatAmount(tradable: Tradable): number {
		if (!this.getFiat()) {
			return 0;
		}
		return this.isIndex(tradable) ? tradable.getPrice() : toCurrencyByFiatId(tradable.getPrice(), this.getFiat());
	}

	public isIndex(tradable: Tradable): boolean {
		return tradable instanceof Index;
	}

	public openPricePage(a: WatchlistAssetViewModel): void {
		this.router.navigateByUrl(this.pathResolverService.assetDetails(a.tradable));
	}

	public addToWatchlist(): void {
		this.watchlistFacade.openAddToWatchlistModal();
	}
}

interface WatchlistAssetViewModel {
	logo: string;
	defaultLogo: string;
	name: string;
	symbol: string;
	colorClass: { chart: string; price: string };
	valueChangePrefixOptions: ValueChangePrefixInterface;
	absoluteChange: number;
	fiatAmount: number;
	fiatPrecisionOptions: FiatPrecisionOptions;
	tradable: Tradable;
}
